import { useState, createContext, useContext, ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import LoadingScreen from './LoadingScreen';

interface LayoutContextType {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within Layout component');
  }
  return context;
};

interface LayoutProps {
  loading?: boolean;
  children: ReactNode;
}

export default function Layout({ loading = false, children }: LayoutProps) {
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showLoading, setShowLoading] = useState(loading);
  const [routeLoading, setRouteLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle initial loading with minimum display time
  useEffect(() => {
    if (loading) {
      setShowLoading(true);
      // Minimum loading time of 800ms to ensure users see the loading screen
      const timer = setTimeout(() => {
        if (!loading) {
          setShowLoading(false);
        }
      }, 800);
      return () => clearTimeout(timer);
    } else {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Handle route changes with brief loading
  useEffect(() => {
    setRouteLoading(true);
    const timer = setTimeout(() => {
      setRouteLoading(false);
    }, 300); // Brief loading for route changes
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Determine current tab from route
  const getCurrentTab = () => {
    if (location.pathname.startsWith('/progress')) return 'progress';
    if (location.pathname.startsWith('/history')) return 'history';
    if (location.pathname.startsWith('/photos')) return 'photos';
    return 'log';
  };

  if (showLoading || routeLoading) {
    return <LoadingScreen />;
  }

  return (
    <LayoutContext.Provider value={{ soundEnabled, setSoundEnabled }}>
      <div className="min-h-screen bg-background">
        <Navigation
          activeTab={getCurrentTab()}
          onTabChange={(tab) => navigate(`/${tab === 'log' ? '' : tab}`)}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          onLogoClick={() => navigate('/')}
        />
        <main className="max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-12">
          {children}
        </main>
      </div>
    </LayoutContext.Provider>
  );
}